import React, { useEffect, useRef } from "react";
import { useTheme } from "@mui/material";

const COLUMNS = 48;
const ROWS = 8;
const CELL = 4;
const GAP = 2;
const IDLE_LEVEL = 0.06;

const createAnalyser = (context, stream) => {
  if (!stream) return null;
  const source = context.createMediaStreamSource(stream);
  const analyser = context.createAnalyser();
  analyser.fftSize = 256;
  analyser.smoothingTimeConstant = 0.75;
  source.connect(analyser);
  return analyser;
};

const levelsFrom = (analyser, buffer) => {
  if (!analyser) return null;
  analyser.getByteFrequencyData(buffer);
  const usable = Math.floor(buffer.length * 0.6);
  const perColumn = Math.max(1, Math.floor(usable / COLUMNS));
  return Array.from({ length: COLUMNS }, (_, column) => {
    let sum = 0;
    for (let i = 0; i < perColumn; i += 1) sum += buffer[column * perColumn + i];
    return Math.min(1, sum / perColumn / 180);
  });
};

const VoiceBars = ({ localStream, remoteStream, active }) => {
  const canvasRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = active && (localStream || remoteStream) ? new (window.AudioContext || window.webkitAudioContext)() : null;
    const local = context ? createAnalyser(context, localStream) : null;
    const remote = context ? createAnalyser(context, remoteStream) : null;
    const buffer = new Uint8Array(local?.frequencyBinCount || remote?.frequencyBinCount || 128);
    const userColor = theme.palette.info.main;
    const agentColor = theme.palette.success.main;
    const idleColor = theme.palette.action.disabled;
    let frame = 0;

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const userLevels = levelsFrom(local, buffer) || [];
      const agentLevels = levelsFrom(remote, buffer) || [];
      const columnWidth = width / COLUMNS;
      const cellWidth = Math.max(2, columnWidth - GAP);

      for (let column = 0; column < COLUMNS; column += 1) {
        const user = userLevels[column] || 0;
        const agent = agentLevels[column] || 0;
        const level = Math.max(user, agent, IDLE_LEVEL);
        const lit = Math.max(1, Math.round(level * ROWS));
        ctx.fillStyle = agent > user && agent > IDLE_LEVEL ? agentColor : user > IDLE_LEVEL ? userColor : idleColor;
        for (let row = 0; row < lit; row += 1) {
          const y = height - (row + 1) * (CELL + GAP);
          ctx.fillRect(column * columnWidth, y, cellWidth, CELL);
        }
      }
      frame = requestAnimationFrame(draw);
    };

    frame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frame);
      if (context) context.close().catch(() => {});
    };
  }, [localStream, remoteStream, active, theme]);

  return <canvas ref={canvasRef} style={{ width: "100%", height: ROWS * (CELL + GAP), display: "block" }} />;
};

export default VoiceBars;
